import type { Feature } from "@/components/feature-builder/FeatureBuilder";
import { TooltipProvider } from "@/components/ui/tooltip";
import { MetadataHeader } from "./MetadataHeader";
import { FeaturesSection } from "./FeaturesSection";
import { GeneralSettingsSection } from "./GeneralSettingsSection";
import { FiltersSettingsSection } from "./FiltersSettingsSection";
import { KernelSettingsSection } from "./KernelSettingsSection";

export interface StudyConfigSummaryProps {
  // Study metadata
  studyName: string;
  nTrials: number;
  symbol: string;
  timeframe: string;
  parallelCount: number;
  minimumTrades: number;
  objectiveFunction: "sortino" | "sharpe";

  // Features
  features: Feature[];

  // Parameters (ranges or single values)
  // Values can be: number (pinned), [number, number] (range), or boolean
  parameters: Record<string, number | [number, number] | boolean>;

  // Track which params are pinned vs optimizing
  pinnedParameters: Record<string, boolean>;

  // Mode for different contexts
  mode?: "review" | "results";
}

export function StudyConfigSummary({
  studyName,
  nTrials,
  symbol,
  timeframe,
  parallelCount,
  minimumTrades,
  objectiveFunction,
  features,
  parameters,
  pinnedParameters,
  mode: _mode = "review", // Reserved for Phase 2: results mode
}: StudyConfigSummaryProps) {
  return (
    <TooltipProvider>
      <div className="space-y-6">
        <MetadataHeader
          studyName={studyName}
          nTrials={nTrials}
          symbol={symbol}
          timeframe={timeframe}
          parallelCount={parallelCount}
          minimumTrades={minimumTrades}
          objectiveFunction={objectiveFunction}
        />

        <FeaturesSection features={features} />

        <GeneralSettingsSection
          parameters={parameters}
          pinnedParameters={pinnedParameters}
        />

        <FiltersSettingsSection
          parameters={parameters}
          pinnedParameters={pinnedParameters}
        />

        <KernelSettingsSection
          parameters={parameters}
          pinnedParameters={pinnedParameters}
        />
      </div>
    </TooltipProvider>
  );
}
